import { useEffect } from 'react';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { motion, AnimatePresence, Variants } from 'motion/react';
import { ScheduleAvailabilityTypes } from '@frontend/api-schedule-availability';
import { useTranslation } from '@frontend/i18n';
import { SCHEDULE_AVAILABILITY_HOURS_MAX_WIDTH } from '@frontend/schedule-availability-hours';
import { Modal, ModalControlModalProps } from '@frontend/design-system';
import { useAddProviderV3Context } from '../../context/AddProviderV3Context';
import { ProviderDetailsForm } from '../../views/manage-provider-v3/provider-details-form';
import { CustomMultiSelectInfiniteDropdownOptionType } from '../CustomMultiSelectInfiniteDropdown';
import { ProviderAppointmentHours } from '../provider-appointment-hours';

type AddPractitionerModalProps = {
  modalProps: ModalControlModalProps;
};

const modalWidth = SCHEDULE_AVAILABILITY_HOURS_MAX_WIDTH;

const variants: Variants = {
  enter: (value) => ({
    x: value ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (value) => ({
    x: value ? '-100%' : '100%',
    opacity: 0,
  }),
};

export const AddPractitionerModal = ({ modalProps }: AddPractitionerModalProps) => {
  const { t } = useTranslation('schedule');

  const {
    onClose,
    locationId,
    providerDetails,
    setProviderDetails,
    setIsProviderHoursPageVisible,
    setProviderWorkingHours,
    providerWorkingHours,
    isProviderHoursPageVisible,
    selectedAppointmentTypes,
    setSelectedAppointmentTypes,
  } = useAddProviderV3Context();

  useEffect(() => {
    if (!modalProps.show) {
      // reset state when modal get closed
      setIsProviderHoursPageVisible(false);
      setProviderDetails({} as Provider);
      setProviderWorkingHours({});
      setSelectedAppointmentTypes([]);
    }
  }, [
    modalProps.show,
    setIsProviderHoursPageVisible,
    setProviderDetails,
    setProviderWorkingHours,
    setSelectedAppointmentTypes,
  ]);

  const handleProviderHoursPageBack = () => {
    setIsProviderHoursPageVisible(false);
  };
  const handleProviderHoursPageContinue = (workingHours: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek) => {
    setProviderWorkingHours(workingHours);
    setIsProviderHoursPageVisible(false);
  };

  const handleSelectProviderHoursButtonClick = (
    providerDetails: Provider,
    selectedAppointmentTypes: CustomMultiSelectInfiniteDropdownOptionType[]
  ) => {
    setProviderDetails(providerDetails);
    setSelectedAppointmentTypes(selectedAppointmentTypes);
    setIsProviderHoursPageVisible(true);
  };

  return (
    <Modal.Responsive {...modalProps} minWidth={modalWidth} maxWidth={modalWidth}>
      <Modal.Header title='test' onClose={onClose}>
        {t('Add Provider')}
      </Modal.Header>
      <Modal.Body css={{ maxHeight: 720, overflow: 'hidden' }}>
        <AnimatePresence custom={isProviderHoursPageVisible} initial={false} mode='popLayout'>
          {!isProviderHoursPageVisible ? (
            <motion.div
              key='providerDetails'
              custom={isProviderHoursPageVisible}
              variants={variants}
              initial='enter'
              animate='center'
              exit='exit'
              transition={{ type: 'tween', duration: 0.4 }}
              css={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              <ProviderDetailsForm
                locationId={locationId}
                providerDetails={providerDetails}
                primaryButtonLabel={t('Save')}
                onClose={onClose}
                providerWorkingHours={providerWorkingHours}
                onSelectProviderWorkingHoursClick={handleSelectProviderHoursButtonClick}
                selectedAppointmentTypes={selectedAppointmentTypes}
              />
            </motion.div>
          ) : (
            <motion.div
              key='providerHours'
              custom={isProviderHoursPageVisible}
              variants={variants}
              initial='enter'
              animate='center'
              exit='exit'
              transition={{ type: 'tween', duration: 0.4 }}
              css={{ maxHeight: '100%', overflowY: 'auto' }}
            >
              <ProviderAppointmentHours
                locationId={locationId}
                selectedProviderHours={providerWorkingHours}
                onBack={handleProviderHoursPageBack}
                onContinue={handleProviderHoursPageContinue}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Modal.Body>
    </Modal.Responsive>
  );
};
